import { alpha } from "@mui/material/styles";
import { backdropClasses } from "@mui/material/Backdrop";
import { filledInputClasses } from "@mui/material/FilledInput";
import { outlinedInputClasses } from "@mui/material/OutlinedInput";
import { paperClasses } from "@mui/material/Paper";
import { tableCellClasses } from "@mui/material/TableCell";
import { common } from "@mui/material/colors";
import type { Components } from "@mui/material/styles/components";
import type {
  PaletteColor,
  PaletteOptions,
} from "@mui/material/styles/createPalette";
import { switchClasses } from "@mui/material/Switch";
import { button, buttonSize, menu, switchControl } from "../colors";

interface Config {
  palette: PaletteOptions;
}

/** Height / padding / radius / type step shared by one button size step. */
const sizeStyles = (size: typeof buttonSize.medium) => ({
  height: `${size.height}px`,
  padding: `0 ${size.paddingX}`,
  borderRadius: size.radius,
  fontSize: size.fontSize,
});

export const createComponents = ({ palette }: Config): Components => {
  return {
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: palette.neutral![200],
          color: palette.text!.primary,
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          [`&:not(.${backdropClasses.invisible})`]: {
            backgroundColor: alpha(palette.neutral![900], 0.75),
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          [`&.${paperClasses.elevation1}`]: {
            boxShadow:
              "rgba(0, 0, 0, 0.04) 0px 5px 22px, rgba(0, 0, 0, 0.03) 0px 0px 0px 0.5px",
            background: common.white,
            borderRadius: "8px",
            border: `1px solid ${palette.divider}`,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        icon: {
          color: palette.action!.active,
        },
        root: {
          borderColor: palette.neutral![200],
          backgroundColor: alpha((palette.primary as PaletteColor).main, 0.08),
          // primary.main is 4.0:1 on this 8% tint; the dark shade clears
          // 4.5:1 for chip text (WCAG 1.4.3).
          color: (palette.primary as PaletteColor).dark,
          borderRadius: "10px",
          border: "1px",
          fontWeight: 500,
          fontSize: "0.875rem",
        },
        deleteIcon: {
          color: alpha((palette.primary as PaletteColor).main, 0.6),
          "&:hover": {
            color: (palette.primary as PaletteColor).main,
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        "#nprogress .bar": {
          backgroundColor: (palette.primary as PaletteColor).main,
        },
        ".slick-dots li button": {
          "&:before": {
            fontSize: 10,
            color: (palette.primary as PaletteColor).main,
          },
        },
        ".slick-dots li.slick-active button": {
          "&:before": {
            color: (palette.primary as PaletteColor).main,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontWeight: 600,
          fontSize: "0.875rem",
          color: palette.text!.primary,
          "&::placeholder": {
            fontWeight: 400,
            fontSize: "0.875rem",
            color: palette.text!.secondary,
            opacity: 1,
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        underline: {
          "&:after": {
            borderBottomColor: palette.text!.primary,
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        input: {
          fontSize: "0.875rem",
          fontWeight: 400,
          lineHeight: 1.57,
          "&::placeholder": {
            fontWeight: 400,
            fontSize: "0.875rem",
            lineHeight: 1.57,
            color: palette.text!.placeholder,
            opacity: 1,
          },
        },
        root: {
          borderRadius: "12px",
          fontWeight: 400,
          fontSize: "0.875rem",
          "&:hover": {
            backgroundColor: palette.action!.hover,
          },
          [`&.${filledInputClasses.disabled}`]: {
            backgroundColor: "transparent",
          },
          [`&.${filledInputClasses.focused}`]: {
            backgroundColor: "transparent",
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              border: "1px solid",
              borderColor: palette.text!.primary,
            },
          },
          [`&.${filledInputClasses.error}`]: {
            borderColor: (palette.error as PaletteColor).main,
            // boxShadow: `${(palette.error as PaletteColor).main} 0 0 0 2px`,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          fontSize: "0.875rem",
          fontWeight: 500,
          lineHeight: 1.57,
          "&::placeholder": {
            fontWeight: 400,
            fontSize: "0.875rem",
            lineHeight: 1.57,
            color: palette.text!.placeholder,
            opacity: 1,
          },
        },
        root: {
          borderRadius: "12px",
          "&:hover": {
            backgroundColor: palette.action!.hover,
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: palette.neutral![200],
            },
          },
          [`&.${outlinedInputClasses.focused}`]: {
            backgroundColor: "transparent",
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              border: "1px solid",
              borderColor: palette.neutral![200],
            },
          },
          [`&.${outlinedInputClasses.disabled}`]: {
            //  border: "1px solid",
            borderColor: palette.neutral![200],
            backgroundColor: "transparent",
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: palette.neutral![200],
            },
          },
          [`&.${filledInputClasses.error}`]: {
            borderColor: (palette.error as PaletteColor).main,
          },
        },
        notchedOutline: {
          borderColor: palette.neutral![200],
        },
      },
    },
    /**
     * The product's one toggle switch, taken from the redesigned Settings
     * screens: a 38×22 pill with a 16px knob inset 3px on every side. Every
     * value comes from `switchControl` in `theme/colors.ts`, so a screen never
     * restyles a switch — it just renders `<Switch>`.
     *
     * MUI's defaults are all larger than the design (58×38 root, 20px thumb,
     * 9px switchBase padding, a translucent hover halo), so each slot below is
     * a deliberate reset rather than a tweak.
     */
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: switchControl.width,
          height: switchControl.height,
          padding: 0,
          display: "inline-flex",
          flexShrink: 0,
          // The focus ring is painted as a spread shadow on the track, so it
          // has to be allowed outside the root's box.
          overflow: "visible",
        },
        switchBase: {
          padding: switchControl.knobInset,
          color: switchControl.knob,
          transition: switchControl.knobTransition,
          // The design's pill has no ripple and no hover halo — MUI's would be
          // a 34px circle around a 22px track.
          "&:hover": { backgroundColor: "transparent" },
          [`&.${switchClasses.checked}`]: {
            transform: `translateX(${switchControl.knobTravel}px)`,
            color: switchControl.knob,
            "&:hover": { backgroundColor: "transparent" },
            [`& + .${switchClasses.track}`]: {
              backgroundColor: switchControl.trackOn,
              opacity: 1,
            },
          },
          // Keyboard-only affordance. The reference is a `disableRipple`
          // ButtonBase with no focus style of its own; without this, removing
          // the ripple would leave keyboard users no feedback at all.
          // `switchClasses` carries no focus-visible key — the state class comes
          // from the underlying ButtonBase, so match it by name.
          [`&.Mui-focusVisible + .${switchClasses.track}`]: {
            boxShadow: `0 0 0 ${switchControl.focusRingWidth} ${switchControl.focusRing}`,
          },
          [`&.${switchClasses.disabled}`]: {
            color: switchControl.knob,
            [`& .${switchClasses.thumb}`]: { boxShadow: "none" },
            [`& + .${switchClasses.track}`]: {
              backgroundColor: switchControl.trackOffDisabled,
              opacity: 1,
            },
            [`&.${switchClasses.checked} + .${switchClasses.track}`]: {
              backgroundColor: switchControl.trackOnDisabled,
              opacity: 1,
            },
          },
        },
        thumb: {
          width: switchControl.knobSize,
          height: switchControl.knobSize,
          borderRadius: "50%",
          boxShadow: switchControl.knobShadow,
        },
        track: {
          borderRadius: switchControl.trackRadius,
          backgroundColor: switchControl.trackOff,
          opacity: 1,
          transition: switchControl.trackTransition,
        },
        // `size="small"` is the design's compact allowlist-row pill. Without
        // this slot MUI's own small geometry (40×24, 16px thumb) would layer
        // on top of the root reset above and desync knob travel from track.
        sizeSmall: {
          width: switchControl.small.width,
          height: switchControl.small.height,
          [`& .${switchClasses.switchBase}`]: {
            padding: switchControl.small.knobInset,
            [`&.${switchClasses.checked}`]: {
              transform: `translateX(${switchControl.small.knobTravel}px)`,
            },
          },
          [`& .${switchClasses.thumb}`]: {
            width: switchControl.small.knobSize,
            height: switchControl.small.knobSize,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: palette.divider,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          [`& .${tableCellClasses.root}`]: {
            backgroundColor: alpha(
              (palette.primary as PaletteColor).main,
              0.08,
            ),
            color: palette.neutral![700],
          },
        },
      },
    },
    // @ts-ignore
    MuiTimelineConnector: {
      styleOverrides: {
        root: {
          backgroundColor: palette.divider,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backdropFilter: "blur(6px)",
          background: (palette.primary as PaletteColor).main,
          fontSize: "0.625rem",
          fontWeight: 400,
          "& .MuiTooltip-arrow": {
            color: (palette.primary as PaletteColor).main,
          },
        },
      },
    },
    // Global button design. Every screen's button chrome — geometry, weight,
    // icon spacing, transitions and the hover / active / focus-visible /
    // disabled states — is defined once here, so pages no longer restyle it.
    // The re-skinned screens keep only their *colour* override (the design's
    // orange accent); everything else below is shared.
    MuiButton: {
      // Buttons are flat by design; drop MUI's ripple elevation everywhere.
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        // Shared across all variants and colours.
        root: {
          fontWeight: button.fontWeight,
          lineHeight: "normal",
          textTransform: "none",
          whiteSpace: "nowrap",
          minWidth: "auto",
          transition: button.transition,
          // Consistent icon ↔ label gap; individual buttons can still override.
          "& .MuiButton-startIcon": { marginLeft: 0, marginRight: button.iconGap },
          "& .MuiButton-endIcon": { marginRight: 0, marginLeft: button.iconGap },
          "&:focus-visible": {
            outline: `2px solid ${button.focusRing}`,
            outlineOffset: "2px",
          },
          // Keep the cursor meaningful; the `disabled` attribute still blocks
          // the click, so re-enabling pointer events is presentation-only.
          "&.Mui-disabled": {
            cursor: "not-allowed",
            pointerEvents: "auto",
          },
        },
        // Primary action. Flat brand fill, no elevation. Colour comes from the
        // app primary palette (per-colour props like `color="error"` still win);
        // height / padding / radius / type come from the `*Size*` slots below.
        contained: {
          background: (palette.primary as PaletteColor).main,
          color: button.color,
          boxShadow: "none",
          // Hover holds the resting fill — the brand colour must not darken.
          // This has to be stated rather than omitted: MUI's own contained rule
          // is `&:hover { backgroundColor: palette[color].dark; boxShadow:
          // shadows[4] }`, so dropping this block hands both the darkening and
          // the elevation straight back.
          "&:hover": {
            background: (palette.primary as PaletteColor).main,
            boxShadow: "none",
          },
          "&:active": {
            background: button.activeBg,
            boxShadow: "none",
          },
          // Without this, the hardcoded `background`/`:hover` above keep the
          // button coloured while disabled — it only turns grey once hover/focus
          // clears. Force the standard disabled look immediately.
          "&.Mui-disabled": {
            background: button.disabledBg,
            color: button.disabledColor,
            boxShadow: "none",
          },
        },
        // Secondary action. White fill, neutral hairline, muted ink label that
        // darkens to brand ink on hover — the shared "Cancel / secondary" look.
        outlined: {
          background: button.secondaryBg,
          color: button.secondaryColor,
          border: `1px solid ${button.secondaryBorder}`,
          boxShadow: "none",
          "&:hover": {
            background: button.secondaryBg,
            borderColor: button.secondaryHoverBorder,
            color: button.secondaryHoverColor,
            boxShadow: "none",
          },
          "&:active": {
            background: button.secondaryBg,
            borderColor: button.secondaryHoverBorder,
          },
          "&.Mui-disabled": {
            background: button.secondaryBg,
            borderColor: button.secondaryBorder,
            color: button.disabledColor,
          },
        },
        // Low-emphasis action.
        text: {
          color: button.textColor,
          "&:hover": { background: button.textHoverBg },
          "&:active": { background: button.textHoverBg },
        },
        // MUI resolves `{variant}Size{Size}` last, so these win over the
        // padding-only `sizeSmall`/`sizeMedium`/`sizeLarge` slots in the base
        // theme and give the size prop real meaning for every variant.
        containedSizeSmall: sizeStyles(buttonSize.small),
        containedSizeMedium: sizeStyles(buttonSize.medium),
        containedSizeLarge: sizeStyles(buttonSize.large),
        outlinedSizeSmall: sizeStyles(buttonSize.small),
        outlinedSizeMedium: sizeStyles(buttonSize.medium),
        outlinedSizeLarge: sizeStyles(buttonSize.large),
        textSizeSmall: sizeStyles(buttonSize.small),
        textSizeMedium: sizeStyles(buttonSize.medium),
        textSizeLarge: sizeStyles(buttonSize.large),
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: 16,
          fontWeight: 600,
          lineHeight: 1.71,
          minWidth: "auto",
          paddingLeft: 0,
          paddingRight: 0,
          textTransform: "none",
          color: palette.text!.secondary,
          "& + &": {
            marginLeft: 32,
          },
          "&.Mui-selected": {
            fontWeight: 600,
            color: (palette.primary as PaletteColor).main,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: (palette.primary as PaletteColor).main,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: 400,
          fontSize: " 0.875rem",
          color: palette.text!.primary,
        },
        asterisk: {
          color: (palette.error as PaletteColor).main,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          borderRadius: "12px",
          "&:hover": {
            backgroundColor: palette.action!.hover,
          },
          [`&.${filledInputClasses.disabled}`]: {
            backgroundColor: "transparent",
          },
          [`&.${filledInputClasses.focused}`]: {
            backgroundColor: "transparent",
          },
          [`&.${filledInputClasses.error}`]: {
            borderColor: (palette.error as PaletteColor).main,
            // boxShadow: `${(palette.error as PaletteColor).main} 0 0 0 2px`,
          },
        },
        option: {
          '&[aria-selected="true"]': {
            backgroundColor: `${(palette.primary as PaletteColor).light} !important`,
            color: palette.text!.primary,
          },
          '&[aria-selected="true"].Mui-focused': {
            backgroundColor: `${(palette.primary as PaletteColor).light} !important`,
          },
          "&.Mui-focused": {
            backgroundColor: (palette.primary as PaletteColor).lightest,
          },
          "&:hover": {
            backgroundColor: (palette.primary as PaletteColor).lightest,
          },
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          background: (palette.primary as PaletteColor).main,
          color: common.white,
          fontWeight: 400,
          fontSize: "0.688rem",
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            backgroundColor: (palette.primary as PaletteColor).main,
          },
          "&.Mui-active": {
            backgroundColor: (palette.primary as PaletteColor).main,
          },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          "&.MuiRadio-root.Mui-checked ": {
            color: (palette.primary as PaletteColor).main,
          },
          "&.MuiPickersDay-root:focus.Mui-selected": {
            backgroundColor: (palette.primary as PaletteColor).main,
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontSize: "13px",
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: (palette.primary as PaletteColor).lightest,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: palette.text!.primary,
        },
      },
    },
    MuiCircularProgress: {
      // WCAG 4.1.2: a spinner renders role="progressbar", which needs a name.
      // Callers can still pass their own aria-label, or aria-hidden when the
      // surrounding control already says it is busy.
      defaultProps: { "aria-label": "Loading" },
      styleOverrides: {
        root: {
          color: palette.text!.primary,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            color: palette.text!.primary,
            backgroundColor: (palette.primary as PaletteColor).lightest,
            "&:hover": {
              backgroundColor: (palette.primary as PaletteColor).lightest,
            },
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        body2: {
          color: palette.text!.secondary,
        },
        caption: {
          color: palette.text!.secondary,
        },
      },
    },
    // Floating menu card — kebab / more menus and select popups alike.
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: `1px solid ${menu.border}`,
          borderRadius: menu.radius,
          boxShadow: menu.shadow,
          backgroundImage: "none",
        },
        list: {
          padding: menu.padding,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          gap: menu.itemGap,
          minHeight: "auto",
          padding: menu.itemPadding,
          borderRadius: menu.itemRadius,
          fontSize: menu.itemFontSize,
          fontWeight: menu.itemFontWeight,
          lineHeight: 1.45,
          color: menu.itemText,
          "&:hover": {
            backgroundColor: menu.itemHoverBg,
            color: menu.itemHoverText,
          },
          "&.Mui-focusVisible": {
            backgroundColor: menu.itemHoverBg,
          },
          "&.Mui-selected": {
            backgroundColor: menu.itemSelectedBg,
            color: menu.itemSelectedText,
            "&:hover": {
              backgroundColor: menu.itemSelectedBg,
            },
          },
          // Destructive rows opt in with `data-danger` so any menu in the app
          // can render a "Void" / "Delete" entry without restyling it locally.
          '&[data-danger="true"]': {
            color: menu.dangerText,
            "&:hover": {
              backgroundColor: menu.dangerHoverBg,
              color: menu.dangerText,
            },
          },
        },
      },
    },
  };
};
